import * as $ from 'jquery';
import {
	DOG_KENNEL_LAYOUT_ID,
	PANELS_DOOR,
	PANELS_WITH_DOOR,
	DOG_KENNEL_DEPTH,
	MONITOR_BARN_NOT_ALLOWED_VERSIONS,
} from '../ssb_constants';

/**
 * Checks whether the given object has the given key.
 *
 * @param {Object} obj - The object to check.
 * @param {*}      key - The key to check for.
 *
 * @return {boolean} Returns `true` if the object has the given key, otherwise `false`.
 */
export const hasKeyInObject = (obj, key) => key in obj;

/**
 * Checks whether the given object is empty.
 *
 * @param {Object} obj - The object to check.
 *
 * @return {boolean} Returns `true` if the object has no enumerable properties, otherwise `false`.
 */
export const isEmptyObject = (obj) => Object.keys(obj).length === 0;

/**
 * Return a copy of the object only containing the properties which pass the
 * given predicate.
 *
 * @param {Object} obj - The object to filter.
 * @param {Function} predicate - A function which takes a value and returns a
 *                               boolean. If the function returns `true`, the
 *                               property is skipped.
 *
 * @return {Object} A new object containing the filtered properties.
 */
export function objectFilter(obj, predicate) {
	let result = {},
		key;

	for (key in obj) {
		if (obj.hasOwnProperty(key) && !predicate(obj[key])) {
			result[key] = obj[key];
		}
	}

	return result;
}

/**
 * Groups an array of objects by the value of the given key.
 *
 * @param {Object[]} xs - The array of objects to group.
 * @param {string} key - The key to group by.
 * @returns {Object} An object with property names derived from the values of the given key and values of arrays of objects with the same key values.
 */
export function groupBy(xs, key: string) {
	return xs.reduce((rv, x) => {
		(rv[x[key]] = rv[x[key]] || []).push(x);

		return rv;
	}, {});
}

/**
 * Checks if a specified key exists in an array.
 *
 * @param {Array<any>} args - The array to search within.
 * @param {any} key - The key to look for in the array.
 * @returns {boolean} True if the key is found in the array, otherwise false.
 */

export function hasKeyInArray(args, key) {
	return -1 !== args.indexOf(key);
}

/**
 * Evaluates whether an item is visible based on its label and price.
 * An item is visible if either its label or key is 'none' (case insensitive),
 * or if its price is 0 or an empty string.
 * @param {string} label The label of the item.
 * @param {string} key The key of the item.
 * @param {number|string} price The price of the item.
 * @returns {boolean} True if the item is visible, false otherwise.
 */
export const shouldHideOptionNone = (label, key, price) => {
	const isHiddenNone: boolean = (<any>window).shedpro.hide_option_none;
	if (!isHiddenNone) return false;

	return isHiddenByLabelAndPrice(label, key, price);
};

/**
 * Evaluates whether an item is visible based on its label and price.
 * An item is visible if either its label or key is 'none' (case insensitive),
 * or if its price is 0 or an empty string.
 * @param {string} label The label of the item.
 * @param {string} key The key of the item.
 * @param {number|string} price The price of the item.
 * @returns {boolean} True if the item is visible, false otherwise.
 */
export const isHiddenByLabelAndPrice = (label, key, price) => {
	return (
		(label.toString().trim().toLowerCase() === 'none' ||
			key.toString().trim().toLowerCase() === 'none') &&
		(price == 0 || price == '')
	);
};

/**
 * Generate a key from a given text string, by:
 * 1. Trimming whitespace from the start and end of the string
 * 2. Converting to lowercase
 * 3. Replacing multiple whitespace characters with a single underscore
 * @param {string} text The text string to generate a key from
 *
 * @returns {string} The generated key
 */
export const generateKey = (text: string) => {
	return text.toString().trim().toLowerCase().replace(/\s+/g, '_');
};

/**
 * Removes special characters from a given string.
 *
 * This function removes all non-alphanumeric characters, leaving only letters and numbers.
 * It will also remove spaces by default.
 *
 * @param {string} str The input string from which special characters will be removed.
 * @returns {string} A string with special characters removed.
 */
export const removeSpecialCharacters = (str) => {
	return str.replace(/[^a-zA-Z0-9]/g, '');
};

/**
 * Capitalize the first letter of a given word.
 *
 * @param {string} word The word to be capitalized.
 * @returns {string} The capitalized word.
 */
export const capitalize = (word) => {
	return word[0].toUpperCase() + word.substring(1).toLowerCase();
};

/**
 * Encodes a string as a Base64 string with Unicode support.
 * @param {string} str - The string to encode.
 * @returns {string} - The Base64 encoded string.
 */
export const b64EncodeUnicode = (str) => {
	return btoa(
		encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, function (match, p1) {
			return String.fromCharCode(Number('0x' + p1));
		}),
	);
};

/**
 * Checks if an item exists in an array, and if not, adds the item to the array.
 * If the given array is not an array, creates a new array with the item.
 *
 * @param {Array<any>} array The array to check and push the item to.
 * @param {any} item The item to check for and add to the array.
 * @returns {Array<any>} The array with the item added, if it did not exist.
 */
export const checkAndPushKeyToArray = (array, item) => {
	if (Array.isArray(array)) {
		if (-1 === array.indexOf(item)) {
			array.push(item);
		}
	} else {
		array = [item];
	}

	return array;
};

/**
 * Get the default lock position for a given component, based on the component's type and name.
 *
 * @param {string} type The type of the component. Only 'door' is currently supported.
 * @param {string} newComponentName The name of the component.
 *
 * @returns {Object} An object containing the default lock position, whether the lock is custom, whether the hinge is enabled, and the price of the lock. If the component type is not 'door', or the default lock position is not 'left' or 'right', the function returns undefined.
 */
export const getDefaultLock = (type, newComponentName) => {
	if ('door' !== type) {
		return undefined;
	}

	const defaultLock = $(`[data-upgrade-component-name="${newComponentName}"]`).attr(
		'data-lock-position',
	);
	if ('left' === defaultLock || 'right' === defaultLock) {
		const enableHinge =
			'1' ==
			$(`[data-upgrade-component-name="${newComponentName}"]`).attr('data-enable-hinge');
		const lockPrice = $(`[data-upgrade-component-name="${newComponentName}"]`).attr(
			'data-lock-price',
		);

		return { defaultLock, customLock: defaultLock, enableHinge, lockPrice };
	}

	return undefined;
};

/**
 * Get lock data from the component.
 * @param compUUID - The UUID of the component.
 * @returns {object|undefined} The lock data.
 */
export const getLockDataByUuid = (compUUID: string): object | undefined => {
	const component = jQuery(`.ssb-option[data-uuid="${compUUID}"]`);
	if (!component.length) return;

	const data = component.data();

	const lockPosition = data.lockPosition;
	if (lockPosition === '' || lockPosition === undefined) return;

	return {
		customLock: lockPosition,
		defaultLock: lockPosition,
		enableHinge: data.enableHinge,
		lockPrice: data.lockPrice,
	};
};

/**
 * Get the default color for a given component, based on the component's name.
 *
 * @param {string} componentUUID The name of the component.
 *
 * @returns {Object} An object containing the key, name, price, components, price type, texture type, texture image, and texture color of the default color for the given component. If no default color is found, an empty object is returned.
 */
export const getDefaultComponentColor = (componentUUID) => {
	let result = {};
	$('.wrap-component-color ul li').each(function (i, e) {
		let components = $(e).data('default-components');
		if (components) {
			components = JSON.parse(window.atob(components));
			if (hasKeyInArray(components, componentUUID)) {
				const key = $(e).data('key');
				const uuid = $(e).data('color-uuid');
				const name = $(e).data('name');
				const price = $(e).data('price');
				const minPrice = $(e).data('min-price');
				const maxPrice = $(e).data('max-price');
				let components = $(e).data('components');
				components = components ? JSON.parse(window.atob(components)) : [];
				const priceType = $(e).data('price-type');
				const textureType = $(e).data('texture-type');
				const textureImage = $(e).data('texture-image');
				const textureColor = $(e).data('texture-color');

				result = {
					key,
					uuid,
					name,
					price,
					minPrice,
					maxPrice,
					components,
					priceType,
					textureType,
					textureImage,
					textureColor,
				};

				return;
			}
		}
	});

	return result;
};

/**
 * Finds the index of the first object in an array where the specified key has a specific value.
 *
 * @param {Array<Object>} object - The array of objects to search through.
 * @param {string} key - The key to check in each object.
 * @param {*} value - The value to match against the specified key in each object.
 * @returns {number} The index of the first matching object, or -1 if no match is found.
 */

export const findIndexObject = (object, key, value) => {
	for (let i = 0; i < object.length; i++) {
		if (object[i][`${key}`] === value) {
			return i;
		}
	}

	return -1;
};

/**
 * Checks if the enable_line_item_cost option is enabled.
 * @returns {boolean} True if enable_line_item_cost is enabled, false otherwise.
 */
export const isEnableLineItemCost = () => {
	return (<any>window).ssb.enable_line_item_cost == '1';
};

/**
 * Converts a given text string into a URL-friendly slug.
 *
 * @param {string} text - The input text to convert to a slug.
 * @returns {string} The converted slug.
 */

export const convertToSlug = (text: string) => {
	return text
		.toLowerCase()
		.replace(/ /g, '-')
		.replace(/[^\w-]+/g, '');
};

/**
 * Filters an array of objects based on a specified key and value.
 *
 * @param {Array<Object>} array - The array of objects to filter.
 * @param {string} key - The key to check in each object.
 * @param {string} value - The value to search for within the specified key's value.
 * @returns {Array<Object>} A new array containing only the objects that contain the specified value in the specified key.
 */
export const filterMeshByName = (array, key, value) => {
	return array.filter((obj) => obj[key].includes(value));
};

/**
 * Checks if the "Runner Direction" feature is enabled.
 *
 * @returns {boolean} True if the feature is enabled, false otherwise.
 */
export const isEnableRunnerDirection = () => (<any>window).shedpro.enable_runner_direction == '1';

/**
 * Checks if the "Show Gridlines" feature is enabled.
 *
 * @returns {boolean} True if the feature is enabled, false otherwise.
 */
export const isShowGridLines = () => (<any>window).shedpro.show_gridlines == '1';

/**
 * Determines if the "Allow Swap Side" option is available.
 *
 * @returns {boolean} True if the "Allow Swap Side" option is available, false otherwise.
 */

export const isAllowSwapSide = () => {
	return $('[name="ssb-group-upgrade-side-gable"]').length > 0;
};

/**
 * Checks if the manufacture feature is enabled.
 *
 * @returns {boolean} True if the manufacture feature is enabled, otherwise false.
 */
export const isEnableManufactureFeature = () => {
	return (<any>window).ssb.is_manufacturer_feature == '1';
};

/**
 * Checks if the "Swap Side" option is enabled.
 *
 * @returns {boolean} True if the "Swap Side" option is enabled, false otherwise.
 */
export const isSwapSide = () => {
	return 'enable' === $('[name="ssb-group-upgrade-side-gable"]:checked').val();
};

/**
 * Gets the label for a given side.
 *
 * @param {string} side - The side (e.g. 'left', 'right', 'front', 'back') to get the label for.
 * @param {string} defaultLabel - The default label to use if no special label is defined.
 * @returns {string} The label for the side, either the default label if no special label is defined or the special label if it is.
 */
export const getSideLabel = (side, defaultLabel) => {
	const dataSideLabel = JSON.parse(atob($('#js-data-side-label').data('side-label')));

	if (dataSideLabel[side]) {
		return dataSideLabel[side];
	}

	return defaultLabel;
};

/**
 * Retrieves the label for a given upgrade component name.
 *
 * @param {string} name - The upgrade component name.
 * @returns {string} The label for the given upgrade component name.
 */
export const getLabel = (name) => {
	return $(`[data-upgrade-component-name="${name}"]`).data('option-value');
};

/**
 * Check if all keys exist in the bones object.
 *
 * @param {string[]} keys   List of keys to check.
 * @param {Object}   bones  Object to check against.
 *
 * @return {boolean} True if all keys exist, false if any one of them does not.
 */
export const isExistBones = (keys, bones) => {
	for (let i = 0; i < keys.length; i++) {
		if (!bones[keys[i]]) {
			return false;
		}
	}

	return true;
};

/**
 * Filters an array of objects to include only unique items based on the 'name' property.
 * If an item has a priority field, it will be prioritized over items without the priority field.
 *
 * @param {Array<Object>} arr - The array of objects to filter.
 * @param {string} priorityField - The field to prioritize when filtering unique items.
 * @returns {Array<Object>} A new array containing only unique items.
 */
export const filterUniqueItems = (arr, priorityField, priorityValue) => {
	const results = [];

	for (let i = 0; i < arr.length; i++) {
		const wall = arr[i];
		const hasName = !!wall.name;
		const index = hasName ? results.findIndex((result) => result.name === wall.name) : -1;
		const priorityData = wall.hasOwnProperty(priorityField) ? wall[priorityField] : [];

		if (
			index === -1 &&
			(hasKeyInArray(priorityData, priorityValue) || 0 === priorityData.length)
		) {
			results.push(wall);
		} else {
			if (hasKeyInArray(priorityData, priorityValue)) {
				results[index] = wall;
			}
		}
	}

	return results;
};

/**
 * Get component Label from Dom
 *
 * @param {String} name name of component
 *
 * @returns {String} label of component
 */
export const getComponentLabel = (name) => {
	const customName = jQuery(`[data-upgrade-component-name='${name}']`).val();
	if (customName) {
		// If component has custom name, then return it
		return customName;
	}

	// If component doesn't have custom name, then return label from name
	return name
		.split('_')
		.map((n) => n.charAt(0).toUpperCase() + n.slice(1))
		.join(' ');
};

/**
 * Converts a date string to a Unix timestamp in seconds.
 *
 * @param {string} dateString Date string to convert.
 * @returns {number} Unix timestamp in seconds.
 */
export const convertDateTime = (dateString) => {
	return Math.floor(new Date(dateString).getTime() / 1000);
};

/**
 * Get version of component from component data
 *
 * @param {String} componentName name of component
 * @param {Object} componentData component data
 *
 * @returns {String} version of component
 */
export const getVersionComponent = (componentName, componentData) => {
	if (
		!componentData ||
		!componentName ||
		!componentData[componentName] ||
		!componentData[componentName].updated_at
	) {
		return '';
	}

	const { updated_at: componentUpdateAt } = componentData[componentName];
	return `?ver=${encodeURIComponent(convertDateTime(componentUpdateAt))}`;
};

/**
 * Retrieves the trim data for a given parent component, restricted to
 * the 'top', 'bottom', and 'left_right' keys if present.
 *
 * @param {string} componentUUID - The name of the parent component to retrieve trim data for.
 * @returns {Record<string, string>} The trim data as a parsed object if available, otherwise an empty object.
 */
export const getCasingTrimData = (componentUUID: string): Record<string, string> => {
	const trimData = jQuery(`input.ssb-option[data-uuid='${componentUUID}']`).data('trim');

	if (!trimData) {
		return {};
	}

	const parsedData = JSON.parse(atob(trimData));
	const allowedKeys = ['top', 'bottom', 'left_right'];
	const result: Record<string, string> = {};

	allowedKeys.forEach((key) => {
		if (key in parsedData) {
			result[key] = parsedData[key];
		}
	});

	return result;
};

/**
 * Retrieves a list of components and their associated trim data
 * from the configurator options.
 *
 * @param {string} type - The type of component to retrieve.
 *
 * @returns {Array} A list of objects containing the component name
 *                  and its associated data.
 */
export const getListComponentInSettings = (type: string) => {
	const components = [];
	$(`.ssb-configurator-upgrade.${type} .component-item .ssb-option`).each(function () {
		const componentName = jQuery(this).data('uuid');
		if (componentName) {
			components.push(componentName);
		}
	});

	return components;
};

/**
 * Retrieves a list of all unique casing trim types from the configurator
 * options for one or more component types (e.g. 'windows', 'door').
 *
 * @param {string | string[]} type - The component type(s) to retrieve casing trims for.
 * @returns {string[]} A list of unique casing trim types.
 */
export const getListCasingTrim = (type: string | string[]): string[] => {
	const types = Array.isArray(type) ? type : [type];
	const listCasingTrim = new Set<string>();

	for (const type of types) {
		const componentList = getListComponentInSettings(type);

		for (const componentUuid of componentList) {
			const casingTrim = getCasingTrimData(componentUuid);
			if (!isEmptyObject(casingTrim)) {
				Object.values(casingTrim as Record<string, string>).forEach((item) => {
					listCasingTrim.add(item);
				});
			}
		}
	}

	return Array.from(listCasingTrim);
};

/**
 * Returns the inner right width for the deluxe porch.
 *
 * This method reads the side wall dimension from the `shedpro` object
 * and returns it as a number. If the value is null or not a number,
 * it returns 4. If the value is 0 or less, it returns 0.001.
 *
 * @returns {number} The inner right width of the deluxe porch.
 */
export const deluxePorchGetInnerRightWidth = () => {
	return getDeluxePorchSideWallDimension();
};

/**
 * Returns the Side Wall Dimension for the deluxe porch.
 *
 * This method reads the side wall dimension from the `shedpro` object
 * and returns it as a number. If the value is null or not a number,
 * it returns 4. If the value is 0 or less, it returns 0.001.
 *
 * @returns {number} The side wall of the deluxe porch.
 */
export const getDeluxePorchSideWallDimension = () => {
	const w = <any>window;
	const data = w.shedpro;
	const sideWallDimension = data['side_wall_dimension'];
	if (null === sideWallDimension) {
		return 4;
	}

	const dimension = Number(sideWallDimension);
	return dimension <= 0 ? 0 : dimension;
};

export const getLShapePorchSizeSettings = () => {
	const porchElement = jQuery('[name="ssb-configurator-upgrade-porch"]:checked');

	const toNum = (value) => {
		if (value === null || value === undefined || value === '') {
			return 0;
		}

		const num = Number(value);
		return isNaN(num) || num <= 0 ? 0 : num;
	};

	return {
		porchDepth: toNum(porchElement.attr('data-porch-size')),
		porchAlleyWidth: toNum(porchElement.attr('data-porch-alley-width')),
		porchWidth: toNum(porchElement.attr('data-porch-side-wall-width')),
	};
};

/**
 * Removes duplicate objects from an array of objects.
 *
 * @param {Array<Object>} arr The array of objects to remove duplicates from.
 * @returns {Array<Object>} A new array with no duplicate objects.
 */
export const removeDuplicates = (arr) => {
	const seen = new Set();
	return arr.filter((point) => {
		const key = JSON.stringify(point); // Works for nested objects too
		return seen.has(key) ? false : seen.add(key);
	});
};

/**
 * Formats a color name by appending its hex code if it is a color picker.
 *
 * @param {string} name - The name of the color.
 * @param {string} type - The type of the color, either 'color_picker' or a material name.
 * @param {string} code - The hex code of the color.
 *
 * @returns {string} The formatted color name.
 */
export const formatColorName = (name, type, code) => {
	return 'color_picker' === type ? `${name} - ${code}` : name;
};

export const isPositiveNumber = (value: number): boolean => !isNaN(value) && value > 0;

export const getComponentNameByUUID = (type, uuid) => {
	return jQuery(`[data-upgrade-component-type="${type}"][data-uuid="${uuid}"]`).attr(
		'data-upgrade-component-name',
	);
};

export const getSelectedRunway = () => {
	return $('.ssb-configurator-upgrade-new .ssb-runway:checked');
};

export const getNumberOfRunways = () => {
	return Number(getSelectedRunway().attr('data-num-runway'));
};

export const getSidesOrientationRunWay = () => {
	const sides = getSelectedRunway().attr('data-sides');
	if (!sides) {
		return [];
	}
	return JSON.parse(atob(sides));
};

/**
 * Returns the name of the mesh for a given side.
 *
 * @param {string} side The side of the shed, e.g. 'top_front', 'top_back', 'side_left', etc.
 *
 * @returns {string} The name of the mesh for the given side, e.g. 'front', 'back', 'left', etc.
 */
export const getMeshNameBySide = (side: string): string => {
	return side.replace('top_', '');
};

/**
 * Retrieves the number of windows from a given window group select.
 *
 * The window group select is expected to be in the format of 'windows_<number>'.
 *
 * @param {string} windowGroupSelect The window group select from the configurator.
 *
 * @returns {number} The number of windows as an integer.
 */
export const getNumberWindow = (windowGroupSelect: string): number => {
	const numberWindow = jQuery(
		`.ssb-configurator-upgrade.windows .child-option-group [data-uuid="${windowGroupSelect}"]`,
	).attr('data-number-window');
	return parseInt(numberWindow) || 0;
};

export const fetchJSON = async (url: string) => {
	try {
		const response = await fetch(url, {
			method: 'GET',
			headers: {
				Accept: 'application/json',
			},
		});

		if (!response.ok) {
			throw new Error(`HTTP error! Status: ${response.status}`);
		}

		return await response.json();
	} catch (error) {
		console.error('Fetch error:', error);
		return null;
	}
};

/**
 * Checks if a given side should apply to a given list of sides.
 * If the list of sides includes 'all', it will return true for all sides.
 * If the list of sides does not include 'all', it will return true
 * if the given side is included in the list.
 *
 * @param {string} side The side to check
 * @param {string[]} sides The list of sides to check against
 * @returns {boolean} True if the given side should apply, false otherwise
 */
export const shouldApplyToSide = (side, sides) => {
	return sides.includes(side) || sides.includes('all');
};

/**
 * A simple sleep function that returns a Promise that resolves after the given number of milliseconds.
 * @param {number} ms - The number of milliseconds to wait before resolving the Promise.
 * @returns {Promise<void>} A Promise that resolves after the given number of milliseconds.
 */
export const sleep = (ms) => {
	return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Returns an array of sides that are blocked by openings (doors or windows).
 * A side is considered blocked if it has an opening (door or window) that is also marked as blocked or a tack door.
 *
 * @param {object} components An object containing the components of the shed, with their properties.
 *
 * @returns {string[]} An array of sides that are blocked by openings.
 */
export const getBlockedSides = (components: object): string[] => {
	const blockedSides: string[] = [];

	for (const key in components) {
		const component = components[key];
		const { side, type, isBlock, isTackDoor } = component;

		const isOpening = type === 'door' || type === 'windows';
		const isBlocked = isBlock || isTackDoor;

		if (isOpening && isBlocked && !blockedSides.includes(side)) {
			blockedSides.push(side);
		}
	}

	return blockedSides;
};

/**
 * Returns the shortest angle between two angles in radians.
 * @param {number} currentAlpha - The current angle in radians.
 * @param {number} alphaTarget - The target angle in radians.
 * @returns {number} The shortest angle between the current angle and the target angle.
 */
export const getShortestAngleTarget = (currentAlpha: number, alphaTarget: number) => {
	const twoPI = 2 * Math.PI;
	let delta = (alphaTarget - currentAlpha) % twoPI;

	if (delta > Math.PI) delta -= twoPI;
	if (delta < -Math.PI) delta += twoPI;

	return currentAlpha + delta;
};

/**
 * Returns an array of sides that have either a door or a window.
 * This function iterates over the given components object and checks
 * if the component is either a door or a window. If it is,
 * it adds the side of the component to the array if it is not already
 * included.
 *
 * @param {object} components - An object containing the components of the shed.
 * @returns {string[]} An array of sides that have either a door or a window.
 */
export const getSidesWithDoorsOrWindows = (components: object): string[] => {
	const sides: string[] = [];

	for (const key in components) {
		const component = components[key];
		const { side, type } = component;
		const isOpening = type === 'door' || type === 'windows';

		if (!sides.includes(side) && isOpening) {
			sides.push(side);
		}
	}

	return sides;
};

/**
 * Returns the difference between two arrays.
 * @param {string[]} source The source array.
 * @param {string[]} excluded The array of elements to exclude from the source array.
 * @returns {string[]} The difference between the source and excluded arrays.
 */
export const getArrayDifference = (source: string[], excluded: string[]): string[] => {
	return source.filter((x) => !excluded.includes(x));
};

/**
 * Returns a string of HTML representing a single item in the center item popup.
 * @param {string} side - The side of the item.
 * @param {string} label - The label of the item.
 * @param {string} id - The id of the item.
 * @returns {string} A string of HTML representing the item.
 */
export const pushElementCenterItemPopup = (side: string, label: string, id: string): string => {
	return `
		<div class="item">
			<input name="center_item" value="${side}" type="checkbox" id="${id}"/>
			<label for="${id}">${label}</label>
		</div>
	`;
};

/**
 * Returns the structural point definitions for a given roof shape.
 *
 * The function defines reference points, faces, and ridges used to construct
 * or identify roof geometry in a 3D model. Each roof shape type returns
 * a specific configuration of points corresponding to its design.
 *
 * @param {string} roofShape - The type of roof shape.
 * @returns {Object} An object containing roof geometry reference
 */
export const getRoofPoints = (roofShape) => {
	let points = {
		start: '',
		end: '',
		faces: [],
		ridges: [],
	};
	if (
		roofShape === 'box_gable' ||
		roofShape === 'open_gable' ||
		roofShape === 'saltbox' ||
		roofShape === 'saltbox_customize'
	) {
		points = {
			start: 'r_b_t',
			end: 'r_f_t',
			faces: [
				{ name: 'left', top: 'r_b_t', bottom: 'r_b_l1' },
				{ name: 'right', top: 'r_b_t', bottom: 'r_b_r1' },
			],
			ridges: [{ name: 'ridge_top', start: 'r_b_t', end: 'r_f_t' }],
		};
	} else if (roofShape === 'flat') {
		points = {
			start: 'r_b_l1',
			end: 'r_f_l1',
			faces: [{ name: 'left', top: 'r_b_l1', bottom: 'r_b_r1' }],
			ridges: [],
		};
	} else if (roofShape === 'gambrel' || roofShape === 'gambrel_accurate_measurements') {
		points = {
			start: 'r_b_t',
			end: 'r_f_t',
			faces: [
				{ name: 'left_top', top: 'r_b_t', bottom: 'r_b_l2' },
				{ name: 'left_bottom', top: 'r_b_l2', bottom: 'r_b_l1' },
				{ name: 'right_top', top: 'r_b_t', bottom: 'r_b_r2' },
				{ name: 'right_bottom', top: 'r_b_r2', bottom: 'r_b_r1' },
			],
			ridges: [
				{ name: 'ridge_top', start: 'r_b_t', end: 'r_f_t' },
				{ name: 'ridge_left', start: 'r_b_l2', end: 'r_f_l2' },
				{ name: 'ridge_right', start: 'r_b_r2', end: 'r_f_r2' },
			],
		};
	}

	return points;
};

/**
 * Returns the Y-axis rotation (in degrees) for a given corner trim name.
 *
 * The rotation determines how a corner trim should be oriented based on its position
 * relative to the object (e.g., front-left, front-right, back-right, back-left).
 *
 * @param {'front-left' | 'front-right' | 'back-right' | 'back-left'} cornerName
 *   The name of the corner position.
 * @returns {number}
 *   The rotation angle around the Y-axis in degrees.
 *   Returns 0 if the corner name is not recognized.
 */
export const getCornerTrimRotationY = (cornerName: string): number => {
	switch (cornerName) {
		case 'front-right':
			return 0;
		case 'front-left':
			return 270;
		case 'back-right':
			return 90;
		case 'back-left':
			return 180;
		default:
			return 0;
	}
};

/**
 * Converts a given value from inches to feet, or returns the specified fallback if the value is not a valid, positive number.
 * @param {number} value - The value to convert.
 * @param {number} fallback - The fallback value if the conversion fails.
 * @returns {number} The converted value, or the fallback if the conversion fails.
 */
export const toFeetOrFallback = (value: number, fallback: number) => {
	return Number.isFinite(value) && value > 0 ? value / 12 : fallback;
};

export const roundUp3 = (num) => Math.ceil(num * 1000) / 1000;

export const getWindowGroupingNumber = (uuid) => {
	return jQuery(
		`.ssb-configurator-upgrade.windows .child-option-group [data-uuid="${uuid}"]`,
	).attr('data-option-value');
};

export const filterByValue = (array, key, value) => {
	return array.filter((obj) => obj[key].includes(value))[0];
};

/**
 * Fetch viewer_show_price via AJAX to bypass page cache (Kinsta, CDN, etc.).
 * Returns a Promise that resolves with the realtime viewer_show_price boolean value.
 */
export const fetchViewerShowPrice = (): Promise<boolean | null> => {
	const ssb_settings = <any>window;
	const url = new URL(window.location.href);
	const data: any = { action: 'get_viewer_show_price' };

	let referer = '';
	if (url.searchParams.has('referer')) {
		referer = url.searchParams.get('referer');
	} else if (document.referrer) {
		try {
			referer = new URL(document.referrer).hostname;
		} catch (e) {}
	}

	if (!referer && window !== window.parent) {
		try {
			referer = new URL(document.location.ancestorOrigins?.[0] || '').hostname;
		} catch (e) {}
	}

	if (referer) {
		data.referer = referer;
		data.parent_ref = referer;
	}

	if (url.searchParams.has('embedding')) {
		data.embedding = url.searchParams.get('embedding');
	}

	return new Promise((resolve) => {
		jQuery
			.post(ssb_settings.ssb.ajax_url, data, (res) => {
				if (res && res.success) {
					resolve(!!res.viewer_show_price);
				} else {
					resolve(null);
				}
			})
			.fail(() => {
				resolve(null);
			});
	});
};

/**
 * Check whether configurator is currently displaying snapshot data
 *
 * Snapshot view mode is enabled when configurator root element
 * contains the `is-snapshot-data` css class.
 *
 * Used to:
 * - Detect shared/snapshot rendering mode
 * - Disable interactive features if needed
 * - Apply snapshot-specific behaviors/UI
 */
export const isSnapshotView = (): boolean => {
	return $('#ssb-configurator').hasClass('is-snapshot-data');
};

export const average = (a: number, b: number): number => {
	return (a + b) / 2;
};

export const hasCommonElement = <T>(arr1: T[], arr2: T[]): boolean => {
	const set = new Set(arr2);
	return arr1.some((item) => set.has(item));
};

export const getCommonValues = <T>(arr1: T[], arr2: T[]): T[] => {
	return arr1.filter((item) => arr2.includes(item));
};

/**
 * Check if the panel is a dog door panel
 * @param name component name
 * @param activeLayout dog kennel layout id
 * @returns boolean
 */
export const isDogDoorPanel = (name: string, activeLayout: string): boolean => {
	return (
		(
			[
				DOG_KENNEL_LAYOUT_ID.OUTPOST,
				DOG_KENNEL_LAYOUT_ID.BASECAMP,
				DOG_KENNEL_LAYOUT_ID.SCOUT,
			] as string[]
		).includes(activeLayout) && PANELS_DOOR.includes(name)
	);
};

/**
 * Retrieves other opening door components from the list of all components.
 * An opening door is a door that is defined as an opening panel with wire.
 *
 * @param {string} currentComponentKey - The key of the component being added or edited.
 * @param {Record<string, any>} allComponents - The dictionary containing all components in the build.
 * @returns {any[]} An array of other opening door components.
 */
export const getOtherOpeningDoors = (
	currentComponentKey: string,
	allComponents: Record<string, any>,
): any[] => {
	const otherOpeningDoors: any[] = [];

	for (const key in allComponents) {
		if (key === currentComponentKey) {
			continue;
		}
		const component = allComponents[key];
		if (component.type === 'door' && PANELS_WITH_DOOR.includes(component.name)) {
			otherOpeningDoors.push(component);
		}
	}

	return otherOpeningDoors;
};

/**
 * Restricts component placement boundaries based on Dog Kennel enclosure layout depth
 *
 * @param {number} limitLeft - Current left boundary limit
 * @param {number} limitRight - Current right boundary limit
 * @param {boolean} isZSide - Whether the side is along the Z axis (left/right walls)
 * @param {boolean} isPositive - Whether side boundary direction is positive
 * @param {string} activeLayout - Current layout
 * @param {number} fullOffset - Current full offset distance
 *
 * @returns {Object} Updated boundary limits
 */
export const applyDogKennelEnclosureLimits = (
	limitLeft: number,
	limitRight: number,
	isZSide: boolean,
	isPositive: boolean,
	activeLayout: string,
	fullOffset: number,
) => {
	if (
		(activeLayout === DOG_KENNEL_LAYOUT_ID.OUTPOST ||
			activeLayout === DOG_KENNEL_LAYOUT_ID.BASECAMP) &&
		isZSide
	) {
		const dogKennelDepth = DOG_KENNEL_DEPTH[activeLayout] ?? 0;
		const baseZ = isPositive ? limitRight : limitLeft;
		const enclosureEdge = baseZ - fullOffset + dogKennelDepth;
		if (isPositive) {
			limitLeft = Math.min(limitLeft, enclosureEdge);
		} else {
			limitRight = Math.min(limitRight, enclosureEdge);
		}
	} else if (activeLayout === DOG_KENNEL_LAYOUT_ID.SCOUT && !isZSide) {
		const dogKennelDepth = DOG_KENNEL_DEPTH[activeLayout] ?? 0;
		const baseX = isPositive ? limitLeft : limitRight;
		const enclosureEdge = baseX + fullOffset - dogKennelDepth;
		if (isPositive) {
			limitRight = Math.max(limitRight, enclosureEdge);
		} else {
			limitLeft = Math.max(limitLeft, enclosureEdge);
		}
	}

	return { limitLeft, limitRight };
};

/**
 * Check model is hard code model
 * @param version current version
 * @param modelType current model type
 * @param activeLayout current active layout
 * @returns boolean
 */
export const isHardCodeModel = (
	version: string,
	modelType: string,
	activeLayout: string,
): boolean => {
	if (MONITOR_BARN_NOT_ALLOWED_VERSIONS.includes(version)) {
		return true;
	}

	const hardCodeModels = [
		'structure_ranch_cabin',
		'structure_aframe_single_gable',
		'structure_aframe_double_gable',
		'structure_dog_kennel',
	];

	if (hardCodeModels.includes(modelType)) {
		return true;
	}

	if (
		(
			[
				DOG_KENNEL_LAYOUT_ID.OUTPOST,
				DOG_KENNEL_LAYOUT_ID.BASECAMP,
				DOG_KENNEL_LAYOUT_ID.SCOUT,
			] as string[]
		).includes(activeLayout)
	) {
		return true;
	}

	return false;
};

/**
 * Checks whether the given name represents a double door.
 *
 * @param {string} name - The name to check.
 * @returns {boolean} True if the name contains "_double_"; otherwise, false.
 */
export const isDoubleDoor = (name?: string): boolean => {
	return name?.includes('_double_') ?? false;
};
